import React, { useCallback, useEffect, useState } from 'react';
import API, { extractErrorMessage } from '../lib/api';
import { formatIdentity } from '../lib/identity';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Check, X, Hourglass } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'cardholder', label: 'Cardholder' },
  { value: 'finance_reviewer', label: 'Finance Reviewer' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'finance_admin', label: 'Finance Admin' },
];

/**
 * Admin panel: review pending access requests submitted by SSO users.
 * Renders nothing when there are no pending requests. Drop into UsersPage.
 */
export default function AccessRequestsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [roleById, setRoleById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/access-requests?status=pending');
      const reqs = res.data || [];
      setRows(reqs);
      setRoleById((prev) => {
        const next = { ...prev };
        reqs.forEach((r) => { if (!next[r.id]) next[r.id] = r.requested_role || 'cardholder'; });
        return next;
      });
    } catch {
      /* non-admins / transient: leave empty */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (r, decision) => {
    setBusy(r.id);
    try {
      await API.put(`/access-requests/${r.id}/review`, {
        decision,
        role: decision === 'approve' ? (roleById[r.id] || 'cardholder') : null,
        add_allowlist: true,
      });
      toast.success(decision === 'approve'
        ? `Approved ${r.name || r.email} as ${roleById[r.id] || 'cardholder'}`
        : `Denied ${r.name || r.email}`);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not update request'));
    } finally {
      setBusy(null);
    }
  };

  if (!loading && rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-white shadow-sm mb-4">
      <div className="px-4 py-3 border-b border-amber-100 flex items-center gap-2">
        <Hourglass size={16} className="text-amber-600" />
        <h2 className="text-sm font-semibold text-slate-800">Pending access requests</h2>
        {rows.length > 0 && (
          <span className="ml-1 rounded-full bg-amber-100 text-amber-700 text-xs px-2 py-0.5">{rows.length}</span>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="px-4 py-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800">{r.name || formatIdentity(r)}</div>
              <div className="text-xs text-slate-400">{formatIdentity(r)}</div>
              {r.reason && <div className="mt-0.5 text-xs text-slate-500">{r.reason}</div>}
            </div>
            <div className="flex items-center gap-2">
              <Select value={roleById[r.id] || 'cardholder'} onValueChange={(v) => setRoleById((p) => ({ ...p, [r.id]: v }))}>
                <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-8 text-xs" disabled={busy === r.id} onClick={() => review(r, 'approve')}>
                <Check size={14} className="mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy === r.id} onClick={() => review(r, 'deny')}>
                <X size={14} className="mr-1" /> Deny
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
